export const TIME_BLOCK_HEIGHT = 20; // px
export const UNSCHEDULED_BLOCK_HEIGHT = 28; // px
export const LABEL_WIDTH = 60; // 좌측 시간/종일 라벨 너비
export const MINUTES_PER_BLOCK = 15;
export const BLOCKS_PER_HOUR = 60 / MINUTES_PER_BLOCK;
export const TOTAL_BLOCKS = 24 * BLOCKS_PER_HOUR;
export const HOUR_MARK = Array.from({ length: TOTAL_BLOCKS }, (_, i) => {
    const hours = Math.floor(i / BLOCKS_PER_HOUR);
    const minutes = (i % BLOCKS_PER_HOUR) * MINUTES_PER_BLOCK;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
});

export const getBlockFromTime = (time: Date) => {
    const hours = time.getHours();
    const minutes = time.getMinutes();
    return hours * BLOCKS_PER_HOUR + Math.floor(minutes / MINUTES_PER_BLOCK);
};

// export const getTimeFromBlock = (block: number, date: Date) => {
//     const hours = Math.floor(block / BLOCKS_PER_HOUR);
//     const minutes = (block % BLOCKS_PER_HOUR) * MINUTES_PER_BLOCK;
//     return new Date(
//         date.getFullYear(),
//         date.getMonth(),
//         date.getDate(),
//         hours,
//         minutes
//     );
// };

/**
 * 블록의 시작점과 끝점을 갖는 객체 배열을 개별 인덱스 배열로 변환
 * 예: [{start: 3, end: 5}, {start: 7, end: 9}] -> [3, 4, 7, 8]
 *
 * @param blocks - 시작점(start)과 끝점(end)으로 정의된 블록 범위 객체 배열.
 *                 각 블록 범위는 start(포함)부터 end(미포함)까지의 범위를 나타냄
 *                 { start: number; end: number }[]
 * @returns 모든 블록 범위에 포함된 개별 인덱스들의 배열, number[]
 *
 * @example
 * // 반환값: [3, 4, 7, 8]
 * blockRangesToIndexArray([{start: 3, end: 5}, {start: 7, end: 9}]);
 */
export const blockRangesToIndexArray = (
    blocks: { start: number; end: number }[] | null
): number[] => {
    if (!blocks) return [];
    const indexes: number[] = [];

    for (const block of blocks) {
        for (let i = block.start; i < block.end; i++) {
            indexes.push(i);
        }
    }

    return indexes;
};

/**
 * 특정 블록이 사용 가능한지 확인하는 함수 (비어있거나, 이 플래너에 이미 저장했던 블록)
 * @param block 확인할 블록 번호
 * @param occupiedBlocksMap 총 블록들 중에 사용되어 있는 블록들의 맵
 * @param plannerId 현재 일정의 ID (자신의 블록은 충돌로 간주하지 않음)
 * @returns 해당 블록이 사용 가능한지 여부
 */
export const isBlockAvailable = (
    block: number,
    occupiedBlocksMap: Map<number, string>,
    plannerId?: string
): boolean => {
    const occupyingPlannerId = occupiedBlocksMap.get(block);
    return !occupyingPlannerId || occupyingPlannerId === plannerId;
};

/**
 * 숫자 배열에서 연속된 범위를 찾아 그룹화하는 함수
 * 예: [1, 5, 6, 10, 11, 12, 15] -> [[1], [5, 6], [10, 11, 12], [15]]
 *
 * @param blocks - 연속성을 파악할 숫자 배열.
 *                 입력된 배열은 정렬되지 않은 상태여도 함수 내부에서 정렬하여 처리함
 *                 number[]
 * @returns 연속된 숫자들을 그룹으로 묶은 2차원 배열, number[][]
 *
 * @example
 * // 반환값: [[1], [5, 6], [10, 11, 12], [15]]
 * findContiguousRanges([1, 5, 6, 10, 11, 12, 15]);
 *
 * @example
 * // 반환값: [[3, 4, 5], [7, 8], [10]]
 * findContiguousRanges([3, 4, 7, 8, 5, 10]);
 */
export const findContiguousRanges = (blocks: number[]) => {
    const sorted = [...blocks].sort((a, b) => a - b);
    const ranges: number[][] = [];
    let currentRange: number[] = [];

    sorted.forEach((blockIndex, i) => {
        if (i === 0 || blockIndex === sorted[i - 1] + 1) {
            currentRange.push(blockIndex);
        } else {
            ranges.push([...currentRange]);
            currentRange = [blockIndex];
        }
    });

    if (currentRange.length > 0) {
        ranges.push(currentRange);
    }

    return ranges;
};

/**
 * 마우스 위치로부터 블록 인덱스를 계산하는 함수
 * @param mouseY 마우스 Y 좌표 (클라이언트 좌표계)
 * @param containerRef 컨테이너 ElementRef
 * @returns 계산된 블록 인덱스 또는 null (컨테이너 밖인 경우)
 */
export const getBlockIndexFromMousePosition = (
    mouseY: number,
    containerRef: React.RefObject<HTMLDivElement>
): number | null => {
    if (!containerRef.current) return null;

    const rect = containerRef.current.getBoundingClientRect();
    const relativeY = mouseY - rect.top;

    // 범위 유효성 검사
    if (relativeY < 0 || relativeY >= rect.height) return null;

    const blockIndex = Math.floor(relativeY / TIME_BLOCK_HEIGHT);
    return Math.max(0, Math.min(blockIndex, TOTAL_BLOCKS - 1));
};

interface ResizeParams {
    /**
     * 사용 가능한 블록
     */
    occupiedBlocksMap: Map<number, string>;

    /**
     * 타겟 플래너의 전체 blocks
     */
    originalBlocks: number[];

    /**
     * 전체 blocks가 아닌, 리사이징 대상 범위의 시작 인덱스
     */
    startIndexOfTargetRange: number;

    /**
     * 리사이즈 드래그를 통해 바뀐 블록의 인덱스,
     * isStartHandle일 경우 startBlock, isEndHandle일 경우 endBlock 값일 것임
     */
    newPosition: number;

    /**
     * 리사이징 핸들 방향 (위/아래),
     * true: 위쪽 핸들, false: 아래쪽 핸들
     */
    isStartHandle: boolean;

    /**
     * 리사이징을 위해 마우스 이동을 했는지 여부,
     * 드래그 없이 핸들 클릭만 했을 때 불필요한 리사이징 방지
     */
    hasMoved: boolean;

    /**
     * 플래너 ID (자신의 블록은 충돌로 간주하지 않음)
     */
    plannerId?: string;
}
interface ResizedBlocksResult {
    targetRangeIndex: number;
    ranges: number[][];
    validBlocks: number[];
    updatedBlocks: number[];
}

export const calculateResizedBlocks = ({
    occupiedBlocksMap,
    originalBlocks,
    startIndexOfTargetRange,
    newPosition,
    isStartHandle,
    hasMoved,
    plannerId,
}: ResizeParams): ResizedBlocksResult => {
    // 1. 기존 블록 연속된 범위 찾기
    const ranges = findContiguousRanges(originalBlocks);

    // 2. 리사이징 대상 범위 찾기
    const targetRangeIndex = ranges.findIndex(range =>
        range.includes(startIndexOfTargetRange)
    );

    if (targetRangeIndex === -1 || !hasMoved) {
        // 리사이징 대상 범위를 찾을 수 없거나 리사이징을 안했다면 종료
        return {
            targetRangeIndex,
            ranges,
            validBlocks: originalBlocks,
            updatedBlocks: originalBlocks,
        };
    }

    const targetRange = ranges[targetRangeIndex];
    const originalStart = Math.min(...targetRange);
    const originalEnd = Math.max(...targetRange);

    // 3. 새 블록 범위 계산
    let newBlocks: number[] = [];
    if (isStartHandle) {
        // 시작점 리사이징: startBlock(새로운 시작점)부터 원래 끝점까지
        newBlocks = Array.from(
            { length: originalEnd - newPosition + 1 },
            (_, i) => newPosition + i
        );
    } else {
        // 끝점 리사이징: 원래 시작점부터 endBlock(새로운 끝점)까지
        newBlocks = Array.from(
            { length: newPosition - originalStart + 1 },
            (_, i) => originalStart + i
        );
    }

    // 4. 충돌 검사 - 가능한 블록만 필터링
    const validBlocks = newBlocks.filter(block =>
        isBlockAvailable(block, occupiedBlocksMap, plannerId)
    );

    // 5.  기존 범위 제거, 새 범위 추가
    const result = originalBlocks.filter(
        block => !ranges[targetRangeIndex].includes(block)
    );

    const updatedBlocks = [...new Set([...result, ...validBlocks])].sort(
        (a, b) => a - b
    );

    return { targetRangeIndex, ranges, validBlocks, updatedBlocks };
};
